import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { handleApiError } from '../utils/api-helpers';
const initialState = {
    lessons: [],
    current_lesson: null,
    status: 'idle',
    error: null,
};
/** Merge student_lesson rows by lesson_id + classroom_id so list/detail stay in sync */
function upsertStudentLessonByLesson(lessons, payload) {
    const idx = lessons.findIndex((l) => l.lesson_id === payload.lesson_id &&
        l.classroom_id === payload.classroom_id);
    if (idx !== -1) {
        const next = [...lessons];
        next[idx] = payload;
        return next;
    }
    return [...lessons, payload];
}
function mergeManyStudentLessons(existing, incoming) {
    let next = existing;
    for (const row of incoming) {
        next = upsertStudentLessonByLesson(next, row);
    }
    return next;
}
// Create student lesson thunk
export const createStudentLesson = createAsyncThunk('studentLessons/createStudentLesson', async (lessonData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/student-lessons', lessonData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Get student lesson by ID thunk
export const getStudentLessonById = createAsyncThunk('studentLessons/getStudentLessonById', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/student-lessons/${id}`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Update student lesson thunk
export const updateStudentLesson = createAsyncThunk('studentLessons/updateStudentLesson', async ({ id, updateData }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/student-lessons/${id}`, updateData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Delete student lesson thunk
export const deleteStudentLesson = createAsyncThunk('studentLessons/deleteStudentLesson', async (id, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/student-lessons/${id}`);
        return id;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Get student lessons by student thunk
export const getStudentLessonsByStudent = createAsyncThunk('studentLessons/getStudentLessonsByStudent', async ({ studentId, params = {} }, { rejectWithValue }) => {
    try {
        const queryParams = {};
        if (params.page)
            queryParams.page = params.page.toString();
        if (params.limit)
            queryParams.limit = params.limit.toString();
        if (params.lesson_id)
            queryParams.lesson_id = params.lesson_id;
        if (params.classroom_id)
            queryParams.classroom_id = params.classroom_id;
        if (params.status)
            queryParams.status = params.status;
        const response = await axiosInstance.get(`/students/${studentId}/lessons`, { params: queryParams });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Get student lessons by classroom thunk
export const getStudentLessonsByClassroom = createAsyncThunk('studentLessons/getStudentLessonsByClassroom', async ({ classroomId, params = {} }, { rejectWithValue }) => {
    try {
        const queryParams = {};
        if (params.page)
            queryParams.page = params.page.toString();
        if (params.limit)
            queryParams.limit = params.limit.toString();
        if (params.student_id)
            queryParams.student_id = params.student_id;
        if (params.lesson_id)
            queryParams.lesson_id = params.lesson_id;
        if (params.status)
            queryParams.status = params.status;
        const response = await axiosInstance.get(`/classrooms/${classroomId}/lessons`, { params: queryParams });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Start student lesson thunk
export const startStudentLesson = createAsyncThunk('studentLessons/startStudentLesson', async (lessonData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/student-lessons/start', lessonData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Complete student lesson thunk
export const completeStudentLesson = createAsyncThunk('studentLessons/completeStudentLesson', async (lessonData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/student-lessons/complete', lessonData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Update student lesson progress thunk
export const updateStudentLessonProgress = createAsyncThunk('studentLessons/updateStudentLessonProgress', async (progressData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/student-lessons/progress', progressData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
const studentLessonsSlice = createSlice({
    name: 'studentLessons',
    initialState,
    reducers: {
        clearCurrentLesson: (state) => {
            state.current_lesson = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createStudentLesson.pending, (state) => { state.status = 'loading'; })
            .addCase(createStudentLesson.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons.push(action.payload);
            state.current_lesson = action.payload;
        })
            .addCase(createStudentLesson.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(getStudentLessonById.pending, (state) => { state.status = 'loading'; })
            .addCase(getStudentLessonById.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_lesson = action.payload;
        })
            .addCase(getStudentLessonById.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(updateStudentLesson.pending, (state) => { state.status = 'loading'; })
            .addCase(updateStudentLesson.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons = upsertStudentLessonByLesson(state.lessons, action.payload);
            if (state.current_lesson?.id === action.payload.id)
                state.current_lesson = action.payload;
        })
            .addCase(updateStudentLesson.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(deleteStudentLesson.pending, (state) => { state.status = 'loading'; })
            .addCase(deleteStudentLesson.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons = state.lessons.filter(lesson => lesson.id !== action.payload);
            if (state.current_lesson?.id === action.payload)
                state.current_lesson = null;
        })
            .addCase(deleteStudentLesson.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(getStudentLessonsByStudent.pending, (state) => { state.status = 'loading'; })
            .addCase(getStudentLessonsByStudent.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const incoming = Array.isArray(action.payload) ? action.payload : [];
            state.lessons = mergeManyStudentLessons(state.lessons, incoming);
        })
            .addCase(getStudentLessonsByStudent.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(getStudentLessonsByClassroom.pending, (state) => { state.status = 'loading'; })
            .addCase(getStudentLessonsByClassroom.fulfilled, (state, action) => {
            state.status = 'succeeded';
            const incoming = Array.isArray(action.payload) ? action.payload : [];
            state.lessons = mergeManyStudentLessons(state.lessons, incoming);
        })
            .addCase(getStudentLessonsByClassroom.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(startStudentLesson.pending, (state) => { state.status = 'loading'; })
            .addCase(startStudentLesson.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons = upsertStudentLessonByLesson(state.lessons, action.payload);
            state.current_lesson = action.payload;
        })
            .addCase(startStudentLesson.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(completeStudentLesson.pending, (state) => { state.status = 'loading'; })
            .addCase(completeStudentLesson.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons = upsertStudentLessonByLesson(state.lessons, action.payload);
            if (state.current_lesson?.id === action.payload.id ||
                (state.current_lesson?.lesson_id === action.payload.lesson_id &&
                    state.current_lesson?.classroom_id === action.payload.classroom_id)) {
                state.current_lesson = action.payload;
            }
        })
            .addCase(completeStudentLesson.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            .addCase(updateStudentLessonProgress.pending, (state) => { state.status = 'loading'; })
            .addCase(updateStudentLessonProgress.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.lessons = upsertStudentLessonByLesson(state.lessons, action.payload);
            if (state.current_lesson?.id === action.payload.id ||
                (state.current_lesson?.lesson_id === action.payload.lesson_id &&
                    state.current_lesson?.classroom_id === action.payload.classroom_id)) {
                state.current_lesson = action.payload;
            }
        })
            .addCase(updateStudentLessonProgress.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        });
    },
});
export const { clearCurrentLesson, clearError } = studentLessonsSlice.actions;
export default studentLessonsSlice.reducer;
